<template>
  <div v-if="visibles.length">
    <div
      v-for="v in visibles"
      :key="v.id_version"
      class="notificacion-card notificacion-clickable"
      @click="toggleExpand(v.id_version!)"
    >
      <div class="notificacion-header">
        <div class="img-modelo">
          <img src="../assets/udi-logo.png" alt="udi-logo" class="logo" />
        </div>

        <div class="contenido">
          <p :class="['titulo', expandido === v.id_version ? 'titulo-expandido' : '']">{{ v.nombre }}</p>
          <p class="descripcion">{{ formatFecha(v.fecha_creacion) }}</p>
        </div>

        <button
          class="cerrar"
          aria-label="Cerrar notificación"
          @click.stop="dismiss(v.id_version)"
        >
          ✕
        </button>
      </div>

      <div v-if="expandido === v.id_version" class="notificacion-acciones">
        <button class="btn-acceder" @click.stop="accederVersion(v)">Acceder</button>
      </div>
    </div>
  </div>

  <div v-else class="sin-notificaciones">
    No hay notificaciones.
  </div>
</template>

<script lang="ts" setup>
/**
 * Tarjeta de notificación individual.
 *
 * Componente de presentación. Recibe sus datos por props o del store del módulo.
 */

import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useModelo } from '@/store/GestionModelo'
import { useGestionVersiones } from '@/store/GestionVersiones'
import type EstructuraModelo from '@/interfaces/EstructuraModelo'
import type Version from '@/interfaces/Versiones'

interface ModeloConVersiones extends EstructuraModelo {
  versions: Version[]
}

const router = useRouter()
const modeloStore = useModelo()
const versionesStore = useGestionVersiones()
const modelos = ref<ModeloConVersiones[]>([])

// IDs de versiones ocultas solo en la UI
const ocultos = ref<Set<number>>(new Set())
const expandido = ref<number | null>(null)

// Normaliza estado quitando tildes y pasando a minúsculas
function normalizarEstado(estado?: string): string {
  return (estado ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim()
}

// Versiones de modelos en edición (estructura aún no aprobada), ordenadas por fecha
// Se filtra por estado del MODELO, no de la versión, porque tras aprobar el modelo
// la versión queda en edicion pero la notificación ya no debe aparecer
const filtrados = computed<Version[]>(() =>
  modelos.value
    .filter(m => normalizarEstado(m.estado) === 'edicion')
    .flatMap(m => (m.versions ?? []))
    .filter(v => v.id_version != null && !ocultos.value.has(v.id_version!))
    .sort((a, b) => parseFecha(b.fecha_creacion) - parseFecha(a.fecha_creacion))
)

// Solo mostramos máximo 6
const visibles = computed<Version[]>(() =>
  filtrados.value.slice(0, 6)
)

onMounted(async () => {
  modelos.value = await modeloStore.getModelo() as ModeloConVersiones[]
})

function dismiss(id?: number) {
  if (typeof id === 'number') {
    ocultos.value.add(id)
    if (expandido.value === id) expandido.value = null
  }
}

function toggleExpand(id: number) {
  expandido.value = expandido.value === id ? null : id
}

function accederVersion(v: Version) {
  if (!v.id_modelo || !v.id_version) return
  router.push({ name: 'EstructuraModelo', params: { id_modelo: v.id_modelo, id_version: v.id_version } })
}

const formatterFecha = new Intl.DateTimeFormat('es-CO', { day: '2-digit', month: '2-digit', year: 'numeric' })

function parseFecha(fecha: string): number {
  const soloFecha = fecha.substring(0, 10)
  if (/^\d{4}-\d{2}-\d{2}$/.test(soloFecha)) {
    const [yyyy, mm, dd] = soloFecha.split('-').map(Number)
    return new Date(yyyy, mm - 1, dd).getTime()
  }
  const t = new Date(fecha).getTime()
  return isNaN(t) ? 0 : t
}

function formatFecha(fecha?: string | null) {
  if (!fecha) return '-'
  const soloFecha = fecha.substring(0, 10)
  if (/^\d{4}-\d{2}-\d{2}$/.test(soloFecha)) {
    const [yyyy, mm, dd] = soloFecha.split('-').map(Number)
    const d = new Date(yyyy, mm - 1, dd)
    return formatterFecha.format(d)
  }
  const d = new Date(fecha)
  return isNaN(d.getTime()) ? fecha : formatterFecha.format(d)
}
</script>

<style>
.notificacion-card {
  display: flex;
  flex-direction: column;
  gap: 0;
  padding: 0.5rem 0.75rem;
  width: 90%;
  max-width: 240px;
  box-shadow: var(--shadow-sm);
  border-radius: var(--radius-md);
  transition: box-shadow var(--transition-fast);
  font-family: var(--font-family);
  background-color: white;
  overflow: hidden;
  position: relative;
  margin: 0.5rem auto;
}

.notificacion-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
}

.notificacion-clickable {
  cursor: pointer;
}

.notificacion-clickable:hover {
  box-shadow: var(--shadow-md, 0 2px 8px rgba(0,0,0,0.15));
}

.notificacion-acciones {
  display: flex;
  justify-content: center;
  padding-top: 0.4rem;
}

.btn-acceder {
  background-color: #eea222;
  color: black;
  border: none;
  border-radius: 8px;
  padding: 0.3rem 0.9rem;
  font-size: 0.7rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.15s ease;
}

.btn-acceder:hover {
  background-color: #0e69a0;
  color: white;
}

.contenido {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  overflow: hidden;
}

.titulo,
.descripcion {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.titulo {
  font-weight: 600;
  font-size: 0.75rem;
  max-width: 140px;
}

.titulo-expandido {
  white-space: normal;
  overflow: visible;
  text-overflow: unset;
  word-break: break-word;
}

.descripcion {
  font-size: 0.65rem;
  color: #818181;
  max-width: 140px;
}

.cerrar {
  background: transparent;
  border: none;
  color: #ccc;
  font-size: 1.25rem;
  cursor: pointer;
  transition: color 0.2s ease;
  margin-left: 0.5rem;
  flex-shrink: 0;
}

.cerrar:hover {
  color: #ac0707;
}

.logo {
  padding: 6px;
  width: 36px;
  height: auto;
}

.img-modelo {
  border: 0.5px solid grey;
  border-radius: 6px;
  flex-shrink: 0;
}

.sin-notificaciones {
  color: #818181;
  font-size: 0.8rem;
  text-align: center;
  margin-top: 0.5rem;
}
</style>
